import type { AiDeckDifficulty } from '../engine/aiDeck'

/** Keys the daily catalog can increment from a duel or a shop action. */
export type MissionMetric =
  | 'played'
  | 'playedAi'
  | 'won'
  | 'wonAiHard'
  | 'wonLp'
  | 'survive2000'
  | 'winFast'
  | 'xp'
  | 'cardsDrawn'
  | 'summons'
  | 'tributes'
  | 'spells'
  | 'traps'
  | 'fusions'
  | 'attacks'
  | 'maxBattleDamageTurn'
  | 'packsOpened'

/**
 * Per-seat counters for one finished duel. Action counts come from the
 * engine; outcome flags are overlaid by the server (or the vs-AI payout).
 */
export type DuelMissionMetrics = Record<MissionMetric, number> & {
  finalLp: number
  /** Every player's turn. Kept for duel logs. */
  turnCount: number
  /**
   * The Turno number on the HUD (`MatchState.turnCount`). Fast-win missions
   * use this, because that is the number the player is reading.
   */
  shownTurn: number
  winReason: string | null
}

const METRIC_CAPS: Record<MissionMetric, number> = {
  played: 1,
  playedAi: 1,
  won: 1,
  wonAiHard: 1,
  wonLp: 1,
  survive2000: 1,
  winFast: 1,
  xp: 500,
  cardsDrawn: 80,
  summons: 40,
  tributes: 20,
  spells: 40,
  traps: 20,
  fusions: 10,
  attacks: 40,
  maxBattleDamageTurn: 16_000,
  packsOpened: 10,
}

const HARD_PLUS: ReadonlySet<AiDeckDifficulty> = new Set([
  'hard',
  'expert',
  'master',
])

function clampInt(value: unknown, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  const n = Math.trunc(value)
  if (n <= 0) return 0
  return n > max ? max : n
}

/** Empty counters for a seat that has not acted yet. */
export function emptyDuelMissionMetrics(): DuelMissionMetrics {
  return {
    played: 0,
    playedAi: 0,
    won: 0,
    wonAiHard: 0,
    wonLp: 0,
    survive2000: 0,
    winFast: 0,
    xp: 0,
    cardsDrawn: 0,
    summons: 0,
    tributes: 0,
    spells: 0,
    traps: 0,
    fusions: 0,
    attacks: 0,
    maxBattleDamageTurn: 0,
    packsOpened: 0,
    finalLp: 0,
    turnCount: 0,
    shownTurn: 0,
    winReason: null,
  }
}

/**
 * Coerce a vs-AI body. Unknown fields become 0. Caps stop a forged
 * payload from claiming more than one day's mission board.
 */
export function parseDuelMissionMetrics(raw: unknown): DuelMissionMetrics {
  const out = emptyDuelMissionMetrics()
  if (!raw || typeof raw !== 'object') return out
  const body = raw as Record<string, unknown>
  for (const key of Object.keys(METRIC_CAPS) as MissionMetric[]) {
    out[key] = clampInt(body[key], METRIC_CAPS[key])
  }
  out.finalLp = clampInt(body.finalLp, 16_000)
  out.turnCount = clampInt(body.turnCount, 200)
  out.shownTurn = clampInt(body.shownTurn, 200)
  const reason = body.winReason
  out.winReason = typeof reason === 'string' && reason.length <= 32
    ? reason
    : null
  return out
}

export type DuelMissionContext = {
  played: boolean
  isAi: boolean
  won: boolean
  difficulty?: AiDeckDifficulty | null
  xp: number
}

/**
 * Fills outcome flags the client must not be trusted to set (win, mode,
 * XP). Keeps engine action counters from `stats`.
 */
export function overlayDuelOutcome(
  stats: DuelMissionMetrics,
  ctx: DuelMissionContext,
): DuelMissionMetrics {
  const won = ctx.won
  const difficulty = ctx.difficulty ?? null
  const hardWin = won && ctx.isAi && difficulty !== null &&
    HARD_PLUS.has(difficulty)
  return {
    ...stats,
    played: ctx.played ? 1 : 0,
    playedAi: ctx.isAi ? 1 : 0,
    won: won ? 1 : 0,
    wonAiHard: hardWin ? 1 : 0,
    wonLp: won && stats.winReason === 'life-points' ? 1 : 0,
    survive2000: won && stats.finalLp >= 2000 ? 1 : 0,
    winFast: won && stats.shownTurn >= 1 && stats.shownTurn <= 8 ? 1 : 0,
    xp: clampInt(ctx.xp, METRIC_CAPS.xp),
    packsOpened: 0,
  }
}

/** Metrics for opening `qty` boosters (no duel). */
export function packOpenMetrics(qty: number): DuelMissionMetrics {
  const out = emptyDuelMissionMetrics()
  out.packsOpened = clampInt(qty, METRIC_CAPS.packsOpened)
  return out
}
